import React, { useEffect, useState } from "react";
import { WifiOff, RefreshCw, CloudUpload, Check } from "lucide-react";
import { subscribeCount } from "../lib/offlineQueue";
import { syncOfflineQueue } from "../lib/api";
import { toast } from "sonner";

export default function OfflineIndicator() {
  const [online, setOnline] = useState(navigator.onLine);
  const [waitingSW, setWaitingSW] = useState(null);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);

  // Online / offline events
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (!reg) return;
        reg.addEventListener("updatefound", () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener("statechange", () => {
            if (newSW.state === "installed" && navigator.serviceWorker.controller) {
              setWaitingSW(newSW);
            }
          });
        });
      });
    }
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Pending queue count
  useEffect(() => {
    const unsubscribe = subscribeCount(setPending);
    const onQueueChange = async () => {
      try {
        const { countPending } = await import("../lib/offlineQueue");
        const c = await countPending(); setPending(c);
      } catch { /* noop */ }
    };
    window.addEventListener("gio:queue-changed", onQueueChange);
    return () => { unsubscribe(); window.removeEventListener("gio:queue-changed", onQueueChange); };
  }, []);

  // Auto-sync on reconnect
  useEffect(() => {
    if (online && pending > 0 && !syncing) {
      runSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, pending]);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await syncOfflineQueue();
      if (r.synced > 0) {
        toast.success(`Synced ${r.synced} pending change${r.synced !== 1 ? "s" : ""}`);
        setJustSynced(true);
        setTimeout(() => setJustSynced(false), 3000);
      }
      if (r.failed > 0 && r.synced === 0) {
        toast.error(`${r.failed} change${r.failed !== 1 ? "s" : ""} failed to sync`);
      }
    } catch { /* noop */ }
    finally { setSyncing(false); }
  };

  const applyUpdate = () => {
    if (waitingSW) {
      waitingSW.postMessage("skipWaiting");
      window.location.reload();
    }
  };

  const visible = !online || waitingSW || pending > 0 || justSynced;
  if (!visible) return null;

  // Banner color & content
  let bg = "#1B96FF", icon = <RefreshCw size={15} />, text = "", actionEl = null;
  if (!online) {
    bg = "#BA0517";
    icon = <WifiOff size={15} />;
    text = pending > 0
      ? `Offline · ${pending} change${pending !== 1 ? "s" : ""} queued`
      : "You're offline — viewing cached data";
  } else if (waitingSW) {
    bg = "#1B96FF";
    icon = <RefreshCw size={15} />;
    text = "New version available";
    actionEl = (
      <button onClick={applyUpdate} className="ml-2 bg-white text-[#1B96FF] px-3 py-0.5 rounded-full text-[12px] font-bold" data-testid="sw-update-btn">
        Reload
      </button>
    );
  } else if (pending > 0) {
    bg = "#FE9339";
    icon = <CloudUpload size={15} className={syncing ? "animate-pulse" : ""} />;
    text = syncing
      ? `Syncing ${pending} change${pending !== 1 ? "s" : ""}…`
      : `${pending} change${pending !== 1 ? "s" : ""} waiting to sync`;
    if (!syncing) {
      actionEl = (
        <button onClick={runSync} className="ml-2 bg-white text-[#FE9339] px-3 py-0.5 rounded-full text-[12px] font-bold" data-testid="manual-sync-btn">
          Sync now
        </button>
      );
    }
  } else if (justSynced) {
    bg = "#2E844A";
    icon = <Check size={15} />;
    text = "All changes synced";
  }

  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-2 px-4 py-2 rounded-full shadow-lg fade-up"
      style={{ background: bg, color: "#fff", fontSize: 13, fontWeight: 600 }}
      data-testid="offline-indicator"
    >
      {icon}
      <span>{text}</span>
      {actionEl}
    </div>
  );
}
